import Editor from '@monaco-editor/react'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { ReactNode, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { ScoreParameterDto } from '../../api/services/qualityControlService'
import { QcTestDefinitionFormValues } from '../../types'
import ScoreParametersEditor from '../ScoreParametersEditor'
import { parseSurveyFields } from '../utils/parseSurveyFields'

export interface ScoreParametersEditorProps {
  parameters: ScoreParameterDto[]
  onChange: (params: ScoreParameterDto[]) => void
}

export interface QualityTestDefinitionFormProps {
  defaultValues: QcTestDefinitionFormValues
  scoreCalculatorTypes: string[]
  onSubmit: (values: QcTestDefinitionFormValues & { scoreParameters: ScoreParameterDto[] }) => void
  isSubmitting: boolean
  /** Static label for the submit button. When `canSchedule=true` the label is derived from
   * the current scheduledActivationDate state and this prop is ignored. */
  submitLabel?: string
  /** When absent the Score Parameters tab is disabled */
  scoreParametersProps?: ScoreParametersEditorProps
  /** Optional banner rendered below the toolbar (e.g. pending version warning) */
  pendingVersionBanner?: ReactNode
  /** Optional error message from the submission action */
  actionError?: string | null
  /** Whether scheduling is allowed */
  canSchedule?: boolean
  /** Version number of the definition being edited (shown under the title) */
  version?: number
}

const QualityTestDefinitionForm = ({
  defaultValues,
  scoreCalculatorTypes,
  onSubmit,
  isSubmitting,
  submitLabel = '',
  scoreParametersProps,
  pendingVersionBanner,
  actionError,
  canSchedule = false,
  version,
}: QualityTestDefinitionFormProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const now = new Date()
  const nowLocal = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16)

  const [displayName, setDisplayName] = useState(defaultValues.displayName)
  const [category, setCategory] = useState(defaultValues.category)
  const [description, setDescription] = useState(defaultValues.description)
  const [displayOrder, setDisplayOrder] = useState(defaultValues.displayOrder)
  const [canTestMultipleEntities, setCanTestMultipleEntities] = useState(defaultValues.canTestMultipleEntities)
  const [scoreCalculatorType, setScoreCalculatorType] = useState(defaultValues.scoreCalculatorType)
  const [targetTestsPerShift, setTargetTestsPerShift] = useState(defaultValues.targetTestsPerShift)
  const [formJson, setFormJson] = useState(defaultValues.formJson)
  const [scheduledActivationDate, setScheduledActivationDate] = useState(defaultValues.scheduledActivationDate)
  const [activeTab, setActiveTab] = useState(0)

  const formSuggestions = useMemo(() => parseSurveyFields(formJson), [formJson])
  const scheduledDateIsInPast = !!scheduledActivationDate && scheduledActivationDate < nowLocal

  const buttonLabel = canSchedule
    ? scheduledActivationDate
      ? t('qualityControl.scheduleActivation')
      : t('qualityControl.saveAndActivate')
    : submitLabel

  const handleSubmit = () => {
    onSubmit({
      displayName,
      category,
      description,
      displayOrder,
      canTestMultipleEntities,
      scoreCalculatorType,
      targetTestsPerShift,
      formJson,
      scheduledActivationDate,
      scoreParameters: scoreParametersProps?.parameters ?? [],
    })
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Sticky toolbar */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 'appBar',
          bgcolor: '#2D2926',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 1,
          gap: 2,
          mx: -3,
          mt: -3,
          mb: 0,
        }}
      >
        <Button
          variant="text"
          size="small"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/quality-control')}
          sx={{ color: '#fff', flexShrink: 0 }}
        >
          {t('qualityControl.back')}
        </Button>
        <Box sx={{ flex: 1, textAlign: 'center' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {displayName || t('qualityControl.editTest')}
          </Typography>
          {version != null && (
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
              {t('qualityControl.version')} {version}
            </Typography>
          )}
        </Box>
        <Button
          variant="contained"
          size="small"
          disabled={isSubmitting}
          onClick={handleSubmit}
          sx={{
            bgcolor: '#FDD106',
            color: '#2D2926',
            fontWeight: 700,
            flexShrink: 0,
            '&:hover': { bgcolor: '#e6bc05' },
            '&:disabled': { bgcolor: '#FDD106', opacity: 0.6 },
          }}
          startIcon={isSubmitting ? <CircularProgress size={14} sx={{ color: '#2D2926' }} /> : undefined}
        >
          {buttonLabel}
        </Button>
      </Box>

      {pendingVersionBanner}

      {actionError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {actionError}
        </Alert>
      )}

      {/* Tab bar */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{
          mt: 1,
          mb: 2,
          '& .MuiTabs-indicator': { backgroundColor: '#FDD106' },
        }}
        textColor="inherit"
      >
        <Tab label={t('qualityControl.definitionTab')} />
        <Tab label={t('qualityControl.formSchemaTab')} />
        {scoreParametersProps == null ? (
          <Tooltip title={t('qualityControl.scoreParametersDisabledTooltip')}>
            <span>
              <Tab label={t('qualityControl.scoreParametersTab')} disabled />
            </span>
          </Tooltip>
        ) : (
          <Tab label={t('qualityControl.scoreParametersTab')} />
        )}
      </Tabs>

      {/* Panel 0: Definition */}
      {activeTab === 0 && (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {t('qualityControl.testIdentitySection')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('qualityControl.testIdentitySectionHelper')}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label={t('qualityControl.displayName')}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
              fullWidth
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label={t('qualityControl.category')}
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              label={t('qualityControl.description')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              rows={2}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label={t('qualityControl.displayOrder')}
              type="number"
              value={displayOrder}
              onChange={(e) => setDisplayOrder(e.target.value)}
              fullWidth
              size="small"
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Divider sx={{ mb: 1 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {t('qualityControl.versionContentSection')}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth size="small" required>
              <InputLabel id="score-calculator-type-label">{t('qualityControl.scoreCalculatorType')}</InputLabel>
              <Select
                labelId="score-calculator-type-label"
                label={t('qualityControl.scoreCalculatorType')}
                value={scoreCalculatorType}
                onChange={(e) => setScoreCalculatorType(e.target.value)}
              >
                {scoreCalculatorTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex', alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={canTestMultipleEntities}
                  onChange={(e) => setCanTestMultipleEntities(e.target.checked)}
                />
              }
              label={t('qualityControl.canTestMultipleEntities')}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label={t('qualityControl.targetTestsPerShift')}
              type="number"
              value={targetTestsPerShift}
              onChange={(e) => setTargetTestsPerShift(e.target.value)}
              fullWidth
              size="small"
              slotProps={{ htmlInput: { min: 0, step: 'any' } }}
            />
          </Grid>
          {canSchedule && (
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label={t('qualityControl.scheduledActivationDate')}
                type="datetime-local"
                value={scheduledActivationDate}
                onChange={(e) => setScheduledActivationDate(e.target.value)}
                fullWidth
                size="small"
                error={scheduledDateIsInPast}
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: nowLocal } }}
                helperText={(() => {
                  if (scheduledDateIsInPast) return t('qualityControl.scheduledDateInPastError')
                  return scheduledActivationDate ? t('qualityControl.schedulingActiveHelper') : undefined
                })()}
              />
            </Grid>
          )}
        </Grid>
      )}

      {/* Panel 1: Form Schema */}
      {activeTab === 1 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 180px)' }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1, flexShrink: 0 }}>
            <Button
              variant="outlined"
              size="small"
              sx={{ color: 'text.primary', borderColor: 'text.primary' }}
              onClick={() => window.open('https://surveyjs.io/create-free-survey', '_blank')}
            >
              {t('qualityControl.openSurveyJSCreator')}
            </Button>
          </Box>
          <Box
            sx={{
              flex: 1,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            <Editor
              language="json"
              defaultValue={formJson}
              onChange={(value) => setFormJson(value ?? '')}
              options={{
                minimap: { enabled: false },
                scrollBeyondLastLine: false,
                fontSize: 13,
                wordWrap: 'off',
                automaticLayout: true,
                tabSize: 2,
              }}
            />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, flexShrink: 0 }}>
            {t('qualityControl.mustBeValidJson')}
          </Typography>
        </Box>
      )}

      {/* Panel 2: Score Parameters */}
      {activeTab === 2 && scoreParametersProps != null && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() =>
                scoreParametersProps.onChange([{ fieldName: '', type: 'boolean' }, ...scoreParametersProps.parameters])
              }
            >
              {t('qualityControl.addParameter')}
            </Button>
          </Box>
          <ScoreParametersEditor
            parameters={scoreParametersProps.parameters}
            onChange={scoreParametersProps.onChange}
            formSuggestions={formSuggestions}
          />
        </Box>
      )}
    </Box>
  )
}

export default QualityTestDefinitionForm
